import { AppMsgActionRules } from "./AppMsgActions";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppAssistRules } from "./AppAssistRules";
import { AppIcons } from "./AppIcons";

// AppComposer - the message box at the foot of the chat card: it grows with what is typed, Enter
// sends on a real keyboard, and a draft from the AI panel can be put into it - or sent from its card.
//
// ⛔ IT NEVER SENDS ON ITS OWN (decision 9). A send starts only on a human act: the Send button,
// Enter pressed on the owner's own keyboard (`AppAssistRules.EnterSends`), or the owner's click on
// a draft card's Send (`AppAssistPanel`, which calls `SendText`). `Fill` - what the card's Edit
// calls - puts text in and focuses the box, and that is ALL it does.
//
// ⭐ ONE SENDER (owner, 2026-09-25: "the Use button in the AI chat should send right away").
// `SendText` is THE way a message leaves this page: the box's own submit (`Submit`) and the draft
// card's Send both call it, so both get the same `/send` request (`IComposerSender`, which is
// `AppMain`), the same validation (`AppOutbox.Build`), the same in-chat bubble and the same
// one-at-a-time latch. ⛔ Do not write a second sender for a new button - call `SendText`.
// ⛔ ONE SEND IN FLIGHT: `Sending` is set BEFORE the request is written and cleared only by its
// answer (`AppComposerSend`). A second press - a double click, Enter while the first uploads, the
// card's Send while the box's is in flight - is refused with a sentence, never a second request
// with a second id, which the bridge's ledger cannot tell from a second message.
// ⚠ A DRAFT SENT FROM ITS CARD LEAVES THE BOX ALONE: only the box's own send empties the box
// (`Queued`), so text the owner was typing survives a draft going out.
//
// ⚠ SHAPED BY INTERFACES, like `AppChatAppend`, so the suite drives it with a fake box and form.

export interface IComposerStyle { height: string; }

export interface IComposerBox {
    value: string;
    scrollHeight: number;
    style: IComposerStyle;
    focus(): void;
    setSelectionRange(Start: number, End: number): void;
    addEventListener(Type: string, Listener: (Ev: Event) => void): void;
}

export interface IComposerForm {
    requestSubmit(): void;
}

/** Who hears how one send went. The box itself is one (it empties on `Queued`); a draft card is another. */
export interface IComposerSent {
    /** The request was accepted (`/send` answered, or the file was written). */
    Queued(): void;
    /** It was not; `Why` is the sentence the page shows. */
    Failed(Why: string): void;
}

/**
 * The page's half of a send: validate, write the request, draw the bubble. Answers "" when the
 * request is on its way (and `Then` is answered exactly once, later), else the sentence to show -
 * and then `Then` is never answered.
 */
export interface IComposerSender {
    /**
     * `Translate` - ⭐ TRANSLATION (2026-09-27): "" = as the chat's chip says (the page decides, asking the service
     * first when it does not know), "yes" = translate (a failed bubble's Retry), "no" = send exactly as typed (a failed
     * bubble's "Send in English"). ⛔ Every one of them is still ONE human click through `AppComposer.SendText`.
     */
    Queue(Text: string, Then: IComposerSent, Translate?: string): string;
    /**
     * A REACTION on the message the page shows as `data-mid` `MessageId` ("" emoji = take it off).
     * Same contract as `Queue`. ⛔ Only `AppComposer.SendReaction` calls it, under the same latch.
     */
    QueueReaction(MessageId: string, Emoji: string, Then: IComposerSent): string;
    /**
     * ⭐ A REPLY / EDIT / DELETE FOR EVERYONE on the message the page shows as `data-mid` `MessageId` (2026-10-14).
     * Same contract as `Queue`. ⛔ Only `AppComposer.SendAction` calls it, under the same latch. Optional: a sender
     * without it refuses those (`NoSendWhy`).
     */
    QueueAction?(Kind: string, MessageId: string, Text: string, Then: IComposerSent): string;
}

/** ⭐ The bar above the box that says what the box is doing now (`AppMsgActionBar`): replying, editing, deleting. */
export interface IComposerModeBar {
    Show(Kind: string, Snippet: string): void;
    Hide(): void;
}

/**
 * One send in flight: releases the composer's latch, then tells whoever pressed Send.
 * ⚠ ANSWERED ONCE - a second answer is dropped, so a stray call cannot release the latch while a
 * later send is in flight.
 */
export class AppComposerSend implements IComposerSent {
    private Composer: AppComposer;
    private Listener: IComposerSent;
    private Done: boolean = false;

    public constructor(Composer: AppComposer, Listener: IComposerSent) {
        this.Composer = Composer;
        this.Listener = Listener;
    }

    public Queued = (): void => { this.Finish("", false); };

    public Failed = (Why: string): void => { this.Finish(nz(Why), true); };

    /** The one ending of a send: answered once, the composer released, then the listener told. */
    private Finish = (Why: string, IsFailure: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Done === true) { return; }
            this.Done = true;
            this.Composer.Release();
            if (IsFailure === true) { this.Listener.Failed(Why); return; }
            this.Listener.Queued();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposerSend.Finish");
        }
    };
}

/** The part of a `KeyboardEvent` the Enter rule reads. */
export interface IComposerKey {
    key: string;
    shiftKey: boolean;
    isComposing: boolean;
    preventDefault(): void;
}

export class AppComposer implements IComposerSent {
    /** Top + bottom border of the box, which `scrollHeight` does not include. */
    public static readonly BorderPx: number = 2;

    private Box: IComposerBox;
    private Form: IComposerForm;
    private Coarse: boolean;
    private Sender: IComposerSender | null;
    /** A send is in flight - see the header. */
    private Sending: boolean = false;
    /** ⭐ What the box is doing: "" = a new message, else `reply` / `edit` / `revoke` on `ModeOn` (a `data-mid`). */
    private ModeKind: string = "";
    private ModeOn: string = "";
    /** The bar that shows the mode; null = no bar (tests, the AI box). */
    public Bar: IComposerModeBar | null = null;

    /**
     * `Coarse` - a touch screen, where Enter is a new line (`AppAssistRules.EnterSends`).
     * `Sender` - null for a box that never sends (the AI panel's question box).
     */
    public constructor(Box: IComposerBox, Form: IComposerForm, Coarse: boolean, Sender: IComposerSender | null) {
        this.Box = Box;
        this.Form = Form;
        this.Coarse = Coarse;
        this.Sender = Sender;
    }

    public Wire = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Box.addEventListener("keydown", this.OnKeyEvent);
            this.Box.addEventListener("input", this.OnInput);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Wire");
        }
    };

    private OnKeyEvent = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.OnKey(Ev as unknown as IComposerKey);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.OnKeyEvent");
        }
    };

    /** Enter (no Shift, real keyboard) submits the form - the SAME handler the Send button runs. */
    public OnKey = (Key: IComposerKey): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppAssistRules.EnterSends(Key.key, Key.shiftKey, Key.isComposing, this.Coarse) !== true) { return; }
            Key.preventDefault();
            this.Form.requestSubmit();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.OnKey");
        }
    };

    private OnInput = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Fit();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.OnInput");
        }
    };

    /** Grow to the text, up to five lines; past that the box scrolls. */
    public Fit = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Box.style.height = "auto";
            this.Box.style.height = String(AppAssistRules.FitPx(this.Box.scrollHeight, AppComposer.BorderPx)) + "px";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Fit");
        }
    };

    /**
     * ⭐ THE SEND. `Text` goes to `/send` through the page's sender; `Listener` hears the answer.
     * Answers "" when it went, else the sentence to show (and `Listener` is not answered).
     * ⛔ Called only from a human act - the form's submit (`Submit`), a draft card's Send click, or a failed
     * translated bubble's Retry / Send in English (`Translate` "yes" / "no" - see `IComposerSender.Queue`).
     */
    public SendText = (Text: string, Listener: IComposerSent, Translate: string = ""): string => {
        return this.Latched("text", nz(Text), "", nz(Translate), Listener);
    };

    /**
     * ⭐ A REACTION (2026-09-25) - the SAME sender and the SAME one-in-flight latch as `SendText`: a
     * reaction is a send (decision 9), so a text and a reaction can never be written at once, and a
     * double tap is refused rather than written twice. ⛔ Called only from a click on the reaction
     * row's emoji (`AppReactions.Pick`). Answers "" when it went, else the sentence to show.
     */
    public SendReaction = (MessageId: string, Emoji: string, Listener: IComposerSent): string => {
        return this.Latched("reaction", nz(MessageId), nz(Emoji), "", Listener);
    };

    /**
     * ⭐ A REPLY / EDIT / DELETE FOR EVERYONE (2026-10-14) - the SAME sender and the SAME latch as `SendText`: each is a
     * send (decision 9). ⛔ Called only from the box's own submit while a mode is set, or the delete bar's "Delete"
     * click (`ConfirmDelete`). Answers "" when it went, else the sentence to show.
     */
    public SendAction = (Kind: string, MessageId: string, Text: string, Listener: IComposerSent): string => {
        return this.Latched("action:" + nz(Kind), nz(MessageId), nz(Text), "", Listener);
    };

    /**
     * ⛔ THE ONE LATCH for every send (text, reaction, action): refused while one is in flight, LATCHED BEFORE the request is
     * written, released when the sender refused. `What` = "text" | "reaction" | "action:<kind>".
     */
    private Latched = (What: string, A: string, B: string, Translate: string, Listener: IComposerSent): string => {
        if (getCoreUtils().KErrorMode === true) { return AppComposer.NoSendWhy(); }
        try {
            if (this.Sender === null) { return AppComposer.NoSendWhy(); }
            const Sender: IComposerSender = this.Sender as IComposerSender;
            const IsAction: boolean = What.indexOf("action:") === 0;
            if (IsAction === true && typeof Sender.QueueAction !== "function") { return AppComposer.NoSendWhy(); }
            if (this.Sending === true) { return AppComposer.BusyWhy(); }
            this.Sending = true;
            const Send: AppComposerSend = new AppComposerSend(this, Listener);
            let Why: string = "";
            if (What === "text") { Why = Sender.Queue(A, Send, Translate); }
            else if (What === "reaction") { Why = Sender.QueueReaction(A, B, Send); }
            else {
                const Queue = Sender.QueueAction as NonNullable<IComposerSender["QueueAction"]>;
                Why = Queue.call(Sender, What.substring(7), A, B, Send);
            }
            if (Why.length > 0) { this.Sending = false; }
            return Why;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Latched");
            this.Sending = false;
            return AppComposer.NoSendWhy();
        }
    };

    /**
     * The box now replies to / edits / deletes the message `MessageId` (a click on the bubble's action). ⛔ SENDS
     * NOTHING: a reply and an edit go only when the owner presses Send; a delete only on the bar's "Delete" click.
     * Edit puts the message's words in the box.
     */
    public SetMode = (Kind: string, MessageId: string, Snippet: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppMsgActionRules.ModeKinds.indexOf(Kind) < 0) { return; }
            this.ModeKind = Kind;
            this.ModeOn = nz(MessageId);
            if (this.Bar !== null) { (this.Bar as IComposerModeBar).Show(Kind, AppMsgActionRules.SnippetOf(Snippet)); }
            if (Kind === "edit") { this.Fill(Snippet); return; }
            if (Kind === "reply") { this.Focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.SetMode");
        }
    };

    /** Back to a new message (the bar's close button, Esc, a send that went). The box keeps its words. */
    public ClearMode = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ModeKind = "";
            this.ModeOn = "";
            if (this.Bar !== null) { (this.Bar as IComposerModeBar).Hide(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.ClearMode");
        }
    };

    /** The mode now ("" = a new message). DOCUMENTED EXCEPTION: reads a field. */
    public ModeNow = (): string => { return this.ModeKind; };

    /** ⛔ The delete bar's "Delete" click - the one way a delete for everyone leaves. */
    public ConfirmDelete = (): string => {
        if (getCoreUtils().KErrorMode === true) { return AppComposer.NoSendWhy(); }
        try {
            if (this.ModeKind !== "revoke") { return ""; }
            return this.SendAction("revoke", this.ModeOn, "", new AppComposerModeSent(this));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.ConfirmDelete");
            return AppComposer.NoSendWhy();
        }
    };

    /** The form was submitted (the Send button, or Enter): send what is in the box. */
    public Submit = (): string => {
        if (getCoreUtils().KErrorMode === true) { return AppComposer.NoSendWhy(); }
        try {
            if (this.ModeKind === "reply" || this.ModeKind === "edit") {
                return this.SendAction(this.ModeKind, this.ModeOn, this.Box.value, this);
            }
            if (this.ModeKind === "revoke") { return AppMsgActionRules.PressDeleteWhy; }
            return this.SendText(this.Box.value, this);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Submit");
            return AppComposer.NoSendWhy();
        }
    };

    /** The send in flight has been answered (`AppComposerSend` only). */
    public Release = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Sending = false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Release");
        }
    };

    /** `IComposerSent` for the box's own send: accepted - empty the box. */
    public Queued = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Clear();
            this.ClearMode();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Queued");
        }
    };

    /**
     * `IComposerSent` for the box's own send: refused. ⚠ THE TEXT STAYS IN THE BOX - the page's
     * hint already says why (`AppMain.OnQueueFailed`); retyping a message the product lost is what
     * would stop the owner trusting the box. Deliberately changes nothing.
     */
    public Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (nz(Why).length === 0) { return; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Failed");
        }
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static BusyWhy = (): string => {
        return "Still sending the last message - wait a moment.";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static NoSendWhy = (): string => {
        return "Nothing can be sent from here.";
    };

    /**
     * Edit (a draft card): the text goes into the box, the caret to its end, the focus to the box.
     * ⛔ NOTHING ELSE. The owner reads it, edits it if they like, and presses Send themselves.
     * ⚠ It replaces what is in the box - which is why the card's own Send never comes here.
     */
    public Fill = (Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Box.value = nz(Text);
            this.Fit();
            this.Focus();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Fill");
        }
    };

    /** A reply-owed task row opened this chat: the caret goes into the box, ready to type. ⛔ Nothing else. */
    public Focus = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Box.focus();
            this.Box.setSelectionRange(this.Box.value.length, this.Box.value.length);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Focus");
        }
    };

    /** After a send was accepted: empty, and back to one line. */
    public Clear = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Box.value = "";
            this.Fit();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposer.Clear");
        }
    };
}

/** The delete bar's send: accepted - the mode ends; refused - it stays (the hint says why). */
export class AppComposerModeSent implements IComposerSent {
    private Composer: AppComposer;
    public constructor(Composer: AppComposer) { this.Composer = Composer; }
    public Queued = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Composer.ClearMode(); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppComposerModeSent.Queued"); }
    };
    /** ERROR-SUPPRESSED-JUSTIFIED: a refusal is already said under the box (`AppMain`); the mode stays for a retry. */
    public Failed = (Why: string): void => { return; };
}

/**
 * The composer bar's markup - the ✦ button (`Spark`, "" without AI help), the box and Send - or, for a
 * chat with no WhatsApp address, the sentence that says why nothing can be sent.
 *
 * ⭐ ONE AI AREA (owner, 2026-09-26): nothing sits above the bar any more - the AI chips and the
 * "let <name> know?" card moved into the AI panel (`AppAssistView.Panel`). ⛔ So this takes no chips and no
 * notes: the bar is the whole foot of the chat card.
 * ⚠ The explanation (`Hint`, the source's own `SendNote` - never an internal name or a command) is a TOOLTIP on
 * Send plus a screen-reader description of the box and the button (`NoteId`, visually hidden).
 * ⛔ NOT AN ALWAYS-VISIBLE LINE (owner, 2026-09-28: "it belongs in a tooltip not taking up ui space"). The line
 * under the box (`wac-said`) starts EMPTY and carries only what has no bubble to live on - a refusal, a request
 * not written, "check your phone" - announced as it changes (`role=status`).
 */
export class AppComposerView {
    /** The visually hidden description the box and Send point at (`aria-describedby`). */
    public static readonly NoteId: string = "wac-sendnote";

    public static readonly AttachId: string = "wac-attachment";
    public static readonly VoiceId: string = "wac-voice";
    /** The chosen file's name and size (text only, written by `AppAttachmentComposer`). */
    public static readonly AttachNameId: string = "wac-attname";
    /** Takes the chosen file away again. Never sends. */
    public static readonly AttachClearId: string = "wac-attclear";
    /** The voice-note choice - shown only while the chosen file is audio. */
    public static readonly VoiceWrapId: string = "wac-voicewrap";
    public static readonly AttachWords: string = "Attach a file";
    public static readonly AttachTip: string = "One file, 8 MB at most. It goes when you press Send.";

    /**
     * ⭐ THE FILE ROW UNDER THE MESSAGE BOX, ON THE DESIGN SYSTEM (audit 2026-10-12). It was the browser's own "Choose
     * File / No file chosen" control and a bare checkbox - the only two things in the signed-in app drawn by the
     * browser instead of by `app.css`, 22px tall on a phone, and "8 MiB" in words nobody uses.
     *   * the picker is the REAL `<input type="file">` (the keyboard, a screen reader and the e2e harness work it), kept
     *     off screen; what is seen is its `<label>` drawn as a `btn secondary sm` with the Plus icon. The label is
     *     `aria-hidden` - the input carries the name - and shows the input's focus ring (`.attachin:focus-visible + label`);
     *   * the chosen file's name and size, and a button that takes it away again, appear only once a file is chosen;
     *   * "Send audio as a voice note" appears only while that file is audio (it is refused for anything else).
     * ⛔ Choosing a file never sends (decision 9): only the Send button's click does.
     */
    public static Attach = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<div class=\"attachment-controls\">"
                + "<input type=\"file\" class=\"attachin\" id=\"" + AppComposerView.AttachId + "\" aria-label=\""
                + AppComposerView.AttachWords + "\" title=\"" + AppComposerView.AttachTip + "\">"
                // ⭐ The + alone (owner 2026-11-02: "+ is enough for attaching a file"); the input carries the name.
                + "<label class=\"attachbtn btn secondary icon\" for=\"" + AppComposerView.AttachId + "\" aria-hidden=\"true\" title=\""
                + AppComposerView.AttachWords + "\">" + AppIcons.Svg(AppIcons.Plus, 16) + "</label>"
                + "<span class=\"attname\" id=\"" + AppComposerView.AttachNameId + "\" hidden></span>"
                + "<button type=\"button\" class=\"attclear btn ghost icon sm\" id=\"" + AppComposerView.AttachClearId
                + "\" aria-label=\"Remove the file\" title=\"Remove the file\" hidden>" + AppIcons.Svg(AppIcons.Close, 16) + "</button>"
                + "<label class=\"voicenote\" id=\"" + AppComposerView.VoiceWrapId + "\" hidden><input type=\"checkbox\" id=\""
                + AppComposerView.VoiceId + "\"><span>Send audio as a voice note</span></label></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposerView.Attach");
            return "";
        }
    };

    public static Bar = (Spark: string, Placeholder: string, Hint: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Note: string = kHTMLCore.EscapeAttr(Hint);
            let Described: string = "";
            let Tip: string = "";
            let Hidden: string = "";
            if (Note.length > 0) {
                Described = " aria-describedby=\"" + AppComposerView.NoteId + "\"";
                Tip = " title=\"" + Note + "\"";
                Hidden = "<span class=\"sr\" id=\"" + AppComposerView.NoteId + "\">" + kHTMLCore.EscapeHtml(Hint) + "</span>";
            }
            // ⭐ ONE ROW (owner 2026-11-02: "make it all fit on one row in the compose panel"): +, the box, Send, ✦.
            return "<form class=\"say\" id=\"wac-say\">" + AppComposerView.Attach()
                // ⭐ No placeholder (owner 2026-11-01: "Remove the message placeholder in the compose box"); the label names it.
                + "<textarea id=\"wac-text\" rows=\"1\" data-wac-to=\"" + kHTMLCore.EscapeAttr(Placeholder)
                + "\" aria-label=\"Message\" autocomplete=\"off\"" + Described + "></textarea><button type=\"submit\" class=\"btn send\" id=\"wac-send\""
                + Tip + Described + ">Send</button>" + Spark + Hidden
                + "<p class=\"sayhint\" id=\"wac-said\" role=\"status\" aria-live=\"polite\"></p></form>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposerView.Bar");
            return "";
        }
    };

    /** No chat id in the index: say so, instead of a box that can send nowhere. */
    public static Off = (Spark: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⛔ NO COMMAND A SUBSCRIBER CANNOT RUN: this said "Rebuild the folder with `npm run dossiers`" - a command
            // deleted with the on-PC tools (2026-09-24). The folder is rebuilt by the service every 15 minutes.
            return "<p class=\"sayoff\">" + Spark + "This chat has no WhatsApp address yet, so nothing can be "
                + "sent to it from here.</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppComposerView.Off");
            return "";
        }
    };
}
